import React, { useMemo } from "react";
import { styled } from "styled-components";
import { LatLng } from "leaflet";
import Map, { MapVehicle } from "../../components/Map/Map";
import {
  displayPosition,
  localizationStdM,
  useTractorsIfAvailable,
} from "../../contexts/TractorContext";
import { Vehicle } from "./data";
import { Card as BaseCard, Eyebrow } from "./styled";
import { tokens } from "./tokens";

/**
 * Where this tractor physically is, beside the record that describes it.
 *
 * Two independent systems meet here, and the join is the reason this is cheap:
 * `TractorContext` keys its live fleet by the **roster name** (`dl13`) — the
 * same key Tractor State uses as its route and partition key — so there is no
 * name-mapping table between them and no third copy of the `av6 → AV-DL-01`
 * lookup.
 *
 * They do not share an identity, though, and that shapes what this can promise.
 * Tractor State reads the fleet_state API under a page-scoped **Cognito**
 * login; positions come from the app's **Auth0** session polling `/tractors`
 * every ten seconds. So this card is best-effort by construction: signed in to
 * one and not the other, it says it has no position rather than pretending the
 * tractor is nowhere.
 *
 * It also states which kind of position it is showing. Connection state lives
 * in the backend process the tractor holds a socket to, so a backend with no
 * sockets (beta, local) reports every vehicle offline and renders the *last
 * known* fix — true, but a different claim from "here it is now", and the
 * difference matters when someone is deciding whether to walk out to it.
 */
interface TractorMiniMapProps {
  vehicle: Vehicle;
  height?: number;
  /** Page-level controls for the header (the maximise toggle). */
  actions?: React.ReactNode;
  /** The card has the whole column: fill it, whether or not there is a fix. */
  expanded?: boolean;
}

const TractorMiniMap: React.FC<TractorMiniMapProps> = ({
  vehicle,
  // A floor, not a height — the card stretches past this when it shares a row
  // with the ticket list. It matters on a narrow screen, where the map has the
  // full width to itself and 190px of it would be a letterbox.
  height = 260,
  actions,
  expanded = false,
}) => {
  const tractors = useTractorsIfAvailable();
  // Memoised so the derived values below have a stable identity to depend on:
  // the fleet map is replaced wholesale every ten-second poll, and without this
  // the marker array was rebuilt on every render of the page around it.
  const tractor = useMemo(
    () => tractors.get(vehicle.vehicleName) ?? null,
    [tractors, vehicle.vehicleName],
  );

  const position = useMemo(
    () => (tractor ? displayPosition(tractor) : null),
    [tractor],
  );
  const online = !!tractor?.statuses.online;

  const mapVehicles: MapVehicle[] = useMemo(() => {
    if (!tractor || !position) return [];
    return [
      {
        id: tractor.displayName || vehicle.vehicleName,
        position: new LatLng(position.lat, position.lon),
        label: tractor.displayName || vehicle.vehicleName,
        action: () => {},
        status: online ? "online" : "offline",
        confidenceRadiusM: localizationStdM(tractor) ?? undefined,
      },
    ];
  }, [tractor, position, online, vehicle.vehicleName]);

  return (
    <Card $fills={!!position} $expanded={expanded}>
      <Header>
        <Eyebrow>Location</Eyebrow>
        <HeaderRight>
          <Status $online={online}>
            {!tractor
              ? "not in the live fleet"
              : online
                ? "live"
                : "last known position"}
          </Status>
          {actions}
        </HeaderRight>
      </Header>
      {position ? (
        <MapFrame $minHeight={height}>
          <Map
            /*
             * Keyed on the tractor, so switching to a sibling builds a new map
             * centred on it. `center` is Leaflet's *initial* view and nothing
             * re-applies it, so without this the rail would swap the record
             * underneath a map still looking at the previous tractor — which is
             * worse than no map, because it looks like an answer.
             *
             * A remount rather than a `setView` on every render, deliberately:
             * position updates arrive every ten seconds, and re-centring on
             * those would yank the view back each time somebody panned away.
             * The view follows the *subject*, not the subject's movement.
             */
            key={vehicle.vehicleName}
            center={new LatLng(position.lat, position.lon)}
            vehicles={mapVehicles}
            isLastKnownPosition={!online}
          />
        </MapFrame>
      ) : (
        /*
         * Deliberately *not* map-sized. Reserving 190px for a map that isn't
         * there costs the top of the context column on every tractor, and this
         * is the common case wherever the backend holds no sockets — a beta
         * environment would show a fleet of empty grey rectangles. One line
         * says the same thing.
         */
        <NoFix>
          {tractor
            ? "No position reported yet."
            : "No live connection to this tractor, so there is no position to show."}
        </NoFix>
      )}
    </Card>
  );
};

/**
 * Fills its grid cell when it has a map to show, and only then.
 *
 * The context row stretches its cards to a common height so they read as one
 * band rather than a ragged edge — but a card whose whole content is "no
 * position reported" has nothing to stretch, and a 500px empty rectangle is
 * exactly what this card was fixed for once already.
 */
const Card = styled(BaseCard)<{ $fills: boolean; $expanded: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: 0;
  /* Maximised, the card is the column and stretches regardless of a fix: an
     empty map is what the reader asked to see big. */
  align-self: ${(p) => (p.$fills || p.$expanded ? "stretch" : "start")};
  ${(p) => (p.$expanded ? "flex: 1;" : "")}
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 20px 10px;
  flex: none;
`;

const HeaderRight = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

const Status = styled.span<{ $online: boolean }>`
  font-size: 12px;
  color: ${(p) => (p.$online ? tokens.readyText : tokens.slate)};
`;

/**
 * The map fills whatever height the card was given.
 *
 * `isolation: isolate` is the load-bearing line. Leaflet puts its panes and
 * controls at z-index 400–1000 in its own stylesheet, which is far above the
 * overlays this page uses (Radix ships them at 50) — so the tiles and the
 * fullscreen button drew straight through the change-history sheet. Isolating
 * here confines that whole range to this card instead of chasing it with an
 * ever-larger z-index on every overlay we add.
 *
 * Height comes from the flex parent rather than a prop: `Map` sizes its
 * container off `height` in pixels, and a card that stretches to match its
 * neighbour doesn't know that number. Leaflet copes because `MapEffect` already
 * watches the container with a ResizeObserver and calls `invalidateSize`.
 */
const MapFrame = styled.div<{ $minHeight: number }>`
  isolation: isolate;
  flex: 1;
  min-height: ${(p) => p.$minHeight}px;
  overflow: hidden;
  border-radius: 0 0 14px 14px;
  border-top: 1px solid ${tokens.line};

  #map-container {
    height: 100%;
  }

  .leaflet-container {
    font-family: ${tokens.fontSans};
  }
`;

const NoFix = styled.div`
  padding: 12px 20px 14px;
  border-top: 1px solid ${tokens.line};
  border-radius: 0 0 14px 14px;
  color: ${tokens.slate};
  font-size: 12.5px;
  line-height: 1.5;
`;

export default TractorMiniMap;
